import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Table } from 'react-bootstrap';

const usersUrl = () => `${(process.env.REACT_APP_BACKEND_URL || '/api').replace(/\/$/, '')}/users`;

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('viewer');
    const [resetPasswords, setResetPasswords] = useState({});
    const [error, setError] = useState('');

    const loadUsers = useCallback(async () => {
        const response = await fetch(usersUrl(), { credentials: 'include' });
        if (!response.ok) throw new Error('Could not load website accounts.');
        setUsers(await response.json());
    }, []);

    useEffect(() => {
        loadUsers().catch(() => setError('Could not load website accounts.'));
    }, [loadUsers]);

    const updateUser = async (user, updates) => {
        const response = await fetch(`${usersUrl()}/${encodeURIComponent(user.id)}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updates),
        });
        if (!response.ok) throw new Error('Could not update this account.');
        const updated = await response.json();
        setUsers((current) => current.map((item) => item.id === user.id ? updated : item));
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        setError('');
        try {
            const response = await fetch(usersUrl(), {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password, role }),
            });
            if (!response.ok) throw new Error('Could not create this account.');
            const created = await response.json();
            setUsers((current) => [...current, created]);
            setUsername('');
            setPassword('');
            setRole('viewer');
        } catch {
            setPassword('');
            setError('Could not create this account. Check the username and try again.');
        }
    };

    const handleUpdate = async (user, updates, clearPassword = false) => {
        setError('');
        try {
            await updateUser(user, updates);
        } catch {
            setError('Could not update this account.');
        } finally {
            if (clearPassword) {
                setResetPasswords((current) => ({ ...current, [user.id]: '' }));
            }
        }
    };

    return (
        <main className="container brand-page mt-5">
            <h1>Website accounts</h1>
            {error && <Alert variant="danger" role="alert">{error}</Alert>}
            <h2>Create account</h2>
            <Form onSubmit={handleCreate} className="mb-4">
                <Form.Group className="mb-2" controlId="new-user-name">
                    <Form.Label>Username</Form.Label>
                    <Form.Control required value={username} onChange={(event) => setUsername(event.target.value)} />
                </Form.Group>
                <Form.Group className="mb-2" controlId="new-user-password">
                    <Form.Label>Temporary password</Form.Label>
                    <Form.Control required type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3" controlId="new-user-role">
                    <Form.Label>Role</Form.Label>
                    <Form.Select value={role} onChange={(event) => setRole(event.target.value)}>
                        <option value="viewer">Viewer</option>
                        <option value="admin">Admin</option>
                    </Form.Select>
                </Form.Group>
                <Button type="submit">Create user</Button>
            </Form>
            <h2>Accounts</h2>
            <Table responsive>
                <thead><tr><th>Username</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                    {users.map((user) => (
                        <tr key={user.id}>
                            <td>{user.username}</td>
                            <td>
                                <Form.Select aria-label={`Role for ${user.username}`} value={user.role}
                                    onChange={(event) => setUsers((current) => current.map((item) => item.id === user.id ? { ...item, role: event.target.value } : item))}>
                                    <option value="viewer">Viewer</option><option value="admin">Admin</option>
                                </Form.Select>
                                <Button size="sm" className="mt-1" onClick={() => handleUpdate(user, { role: user.role })}>Save {user.username} role</Button>
                            </td>
                            <td>{user.enabled ? 'Enabled' : 'Disabled'}</td>
                            <td>
                                <Button size="sm" variant="secondary" onClick={() => handleUpdate(user, { enabled: !user.enabled })}>
                                    {user.enabled ? `Disable ${user.username}` : `Enable ${user.username}`}
                                </Button>
                                <Form.Group className="mt-2" controlId={`reset-${user.id}`}>
                                    <Form.Label>Reset password for {user.username}</Form.Label>
                                    <Form.Control type="password" autoComplete="new-password" value={resetPasswords[user.id] || ''}
                                        onChange={(event) => setResetPasswords((current) => ({ ...current, [user.id]: event.target.value }))} />
                                </Form.Group>
                                <Button size="sm" className="mt-1" disabled={!resetPasswords[user.id]}
                                    onClick={() => handleUpdate(user, { password: resetPasswords[user.id] }, true)}>
                                    Reset {user.username} password
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </main>
    );
}
